// src/features/tasks/components/ApplicationTasks.tsx
import { useEffect, useState } from "react";
import { Task, TaskStatus } from "../types";
import { getTasksByResourceId } from "../services/tasksService";
import Snipper from "@components/ui/Snipper";
import { useToast } from "@customHooks/useToast";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "@components/shadcn-ui/table";
import { Badge } from "@components/shadcn-ui/badge";
import { Button } from "@components/shadcn-ui/button";
import { Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { MobileTaskCard } from "./MobileTaskCard";
import {getResourcePath} from "@lib/utils";

interface ApplicationTasksProps {
    resourceId: string;
    resourceType: string;
}

const getStatusColor = (status: TaskStatus): string => {
    const colorMap: Record<TaskStatus, string> = {
        OPEN: "bg-blue-200 text-blue-900",
        IN_PROGRESS: "bg-yellow-200 text-yellow-900",
        COMPLETE: "bg-green-200 text-green-900",
        CANCELLED: "bg-red-200 text-red-900",
    };
    return colorMap[status];
};

export const ApplicationTasks = ({resourceId}: ApplicationTasksProps) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [isTasksLoading, setIsTasksLoading] = useState(true);
    const {toast} = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTasks = async () => {
            try {
                const fetchedTasks = await getTasksByResourceId(resourceId);
                setTasks(fetchedTasks);
            } catch (error) {
                console.error("Error fetching tasks:", error);
                toast({
                    variant: "destructive",
                    description: "Failed to fetch tasks",
                });
            } finally {
                setIsTasksLoading(false);
            }
        };

        if (resourceId) {
            fetchTasks();
        }
    }, [resourceId]);

    if (isTasksLoading) {
        return (
            <div className="flex items-center justify-center p-4">
                <Snipper />
                <span className="ml-2">Loading tasks...</span>
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div className="text-center p-4 text-gray-500">
                No tasks found for this application
            </div>
        );
    }

    return (
        <div>
            {/* Desktop View */}
            <div className="hidden md:block border rounded-md">
                <Table>
                    <TableHeader className="bg-blue-200">
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Assignee</TableCell>
                            <TableCell>Author</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {tasks.map((task) => (
                            <TableRow key={task.taskId}>
                                <TableCell className="max-w-[200px] truncate" title={task.title}>
                                    {task.title}
                                </TableCell>
                                <TableCell>{task.type}</TableCell>
                                <TableCell>{task.assignee}</TableCell>
                                <TableCell>{task.author}</TableCell>
                                <TableCell>
                                    {new Date(task.dueDateInUTCEpoch).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        className={`rounded-sm px-1 font-normal ${getStatusColor(task.status)}`}
                                    >
                                        {task.status.replace('_', ' ')}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <Link to={`${getResourcePath(task)}/${resourceId}/tasks/${task.taskId}`}>
                                        <Button size="sm" className="cursor-pointer bg-black hover:bg-grey flex justify-center items-center relative">
                                            <Eye className="h-4 w-4 mr-2"/> View
                                        </Button>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden">
                <MobileTaskCard
                    tasks={tasks}
                />
            </div>
        </div>
    );
};
